import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, Card, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Appointment } from '@uplift/api';
import { DateFormats, ROUTING_CONFIG, TimeFormats } from '@uplift/constants';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { trackCloseSessionClick, useIsMobile, useL10n } from '@uplift/hooks';
import { ConnectionTips } from '@uplift/video-call';
import { useUserProfileQuery, useUserQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { UserRole } from '@uplift/types';
import { Button, ProviderCancelSessionModal } from '@uplift/ui-v2';
import { formatUserFullName, getUserFullName } from '@uplift/utils';

import { NoSessionOccurredModal } from '../no-session-occurred-modal';

export type UserSessionInfoCardProps = {
  appointment: Pick<Appointment, 'id' | 'fromDatetime' | 'toDatetime' | 'patientId' | 'providerId'>;
  reEnterCall?: () => void;
  completeSession?: (sessionId: string) => void;
  showActions?: boolean;
};


export const UserSessionInfoCard: React.VFC<UserSessionInfoCardProps> = ({ appointment, reEnterCall, completeSession, showActions = true }) => {
  const [ visible, setVisible ] = useState<'noSessionOccurred' | 'cancel' | null>(null);
  const history = useHistory();
  const l10n = useL10n();
  const { classes } = useStyles();
  const timezone = useTimezone();
  const me = useCurrentuser();
  const isMobile = useIsMobile('sm');
  const localStartTime = CalendarDateService.date(appointment?.fromDatetime).tz(timezone);
  const localEndTime = CalendarDateService.date(appointment?.toDatetime).tz(timezone);
  const date = localStartTime.format(DateFormats['dddd, MMMM D, YYYY']);
  const startTime = localStartTime.format(TimeFormats['h:mm']);
  const endTime = localEndTime.format(TimeFormats['h:mm']);
  const meridiem = localEndTime.format('a').replace(/(.{1})/g, '$1.');

  const { data: patient } = useUserQuery(appointment?.patientId, { enabled: !!appointment?.patientId });
  const { data: patientProfile } = useUserProfileQuery(appointment?.patientId, { enabled: !!appointment?.patientId });
  const { data: provider } = useUserQuery(appointment?.providerId, { enabled: !!appointment?.providerId });
  const { data: providerProfile } = useUserProfileQuery(appointment?.providerId, { enabled: !!appointment?.providerId });
  const userName = useMemo(() => {
    if (me.id === appointment.patientId) {
      if (!provider || !providerProfile) return '';
      return formatUserFullName({ firstName: provider.firstName, lastName: provider.lastName, preferredFirstName: providerProfile.preferredName, fallback: provider.email });
    }

    if (!patient || !patientProfile) return '';
    return formatUserFullName({ firstName: patient.firstName, lastName: patient.lastName, preferredFirstName: patientProfile.preferredName, fallback: patient.email });
  }, [ me.id, appointment.patientId, patient, patientProfile, provider, providerProfile ]);

  const handleCompleteSession = () => {
    trackCloseSessionClick({
      userType: UserRole[me?.role]
    });
    completeSession(appointment?.id ?? '');
  };

  return (
    <>
      <Card variant="outlined" className={showActions ? classes.cardContent : classes.sessionNoteCard}>
        <Box flex={1}>
          <Typography variant="h5" className={classes.userName}>{userName}</Typography>
          <Box className={classes.sessionPeriod}>
            <Typography variant="body2">
              {date}{isMobile ? null : <span>&nbsp;</span>}
            </Typography>
            <Typography variant="body2">
              {startTime}&ndash;{endTime}&nbsp;{meridiem}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.actions}>
          {showActions && (
            <>
              <Button
                color="secondary"
                variant="contained"
                size="large"
                fullWidth
                onClick={handleCompleteSession}
              >
                {l10n.sessions.endSession}
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                size="large"
                fullWidth
                onClick={reEnterCall}
              >
                {l10n.sessions.rejoinSession}
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            color="error"
            size="large"
            onClick={() => setVisible('noSessionOccurred')}
            fullWidth
          >
            {l10n.sessions.noSessionOccurred}
          </Button>
        </Box>
      </Card>
      {showActions && (
        <ConnectionTips />
      )}
      {visible === 'noSessionOccurred' && (
        <NoSessionOccurredModal
          isVisible
          closeModal={() => setVisible(null)}
          goToCancelSession={() => setVisible('cancel')}
        />
      )}
      {visible === 'cancel' && (
        <ProviderCancelSessionModal
          open
          fromDatetime={appointment?.fromDatetime}
          appointmentId={appointment.id}
          onSubmit={async () => history.push(ROUTING_CONFIG.main)}
          onClose={() => setVisible(null)}
          patientDisplayName={getUserFullName(
            patient?.firstName,
            patient?.lastName,
            patientProfile?.preferredName
          )}
          patientId={patient?.id}
          patientStatus={patient?.status}
        />
      )}
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  cardContent: {
    padding: theme.spacing(2, 2),
    maxWidth: theme.spacing(30),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
      textAlign: 'center',
      padding: theme.spacing(2),
    }
  },
  sessionNoteCard: {
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
      flexDirection: 'column',
      textAlign: 'center',
      padding: theme.spacing(2),
    },
  },
  userName: {
    fontWeight: 450,
    fontSize: '20px'
  },
  sessionPeriod: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'center',
    }
  },
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.spacing(32),
    }
  }
}));
